import { Grid, Typography } from "@material-ui/core";
import React from "react";
import { WrappedComponentProps, injectIntl } from "react-intl";
import { PropertyNames } from "../../../data/constants";
import { ExpertMeViewModel } from "../../../model/ExpertMeViewModel";
import { GetTranslation } from "../../../translations/GetTranslation";
import { CompanySelect } from "../../common/customAutocomplete/CompanySelect";
import { GenderSelect } from "../../common/customAutocomplete/GenderSelect";
import GridTextField from "../../common/customGrid/GridTextField";
import DatePicker from "../../common/datePicker/DatePicker";
import AhvNoTextField from "../../common/textField/AhvNoTextField";
import CiceroNoTextField from "../../common/textField/CiceroNoTextField";
import EmailTextField from "../../common/textField/EmailTextField";
import PhoneNoTextField from "../../common/textField/PhoneNoTextField";
import TranslatedMessage from "../../translations/TranslatedMessage";
import { RequiredExpert } from "../ExpertConstants";

type ExpertPersonalFieldsProps = {
  handleChange: (change: Partial<ExpertMeViewModel>) => void;
  handleAhvIsValid: (isValid: boolean) => void;
  handleCiceroIsValid: (isValid: boolean) => void;
  handleEmailIsValid: (isValid: boolean) => void;
  handlePhoneIsValid: (isValid: boolean) => void;
  handleMobileIsValid: (isValid: boolean) => void;
  expert: ExpertMeViewModel;
  requiredFields: RequiredExpert;
} & WrappedComponentProps;

const ExpertPersonalFields = (props: ExpertPersonalFieldsProps) => {
  const { expert, requiredFields, intl, handleChange } = props;

  const handleCompanyChanged = (value?: string) => handleChange({ employerInformation: value });

  return (
    <>
      <Typography variant="h6">
        <TranslatedMessage id="person.section.personalDetails" />
      </Typography>
      <Grid container={true} spacing={4}>
        <GridTextField
          error={requiredFields.isFirstnameEmpty}
          required
          label={GetTranslation(intl, "person.field.firstName")}
          xs={12}
          sm={6}
          md={4}
          onChange={(event) => handleChange({ [PropertyNames.firstName]: event.target.value })}
          value={expert.firstName || ""}
        />
        <GridTextField
          error={requiredFields.isLastnameEmpty}
          required
          label={GetTranslation(intl, "person.field.lastName")}
          xs={12}
          sm={6}
          md={4}
          onChange={(event) => handleChange({ [PropertyNames.lastName]: event.target.value })}
          value={expert.lastName || ""}
        />
        <GenderSelect
          xs={12}
          sm={6}
          md={4}
          error={requiredFields.isGenderEmpty}
          required
          value={expert.gender}
          onChange={(value) => handleChange({ gender: value })}
        />
        <Grid item={true} xs={12} sm={6} md={4} style={{ paddingTop: 0, paddingBottom: 0 }}>
          <DatePicker
            label={<TranslatedMessage id="person.field.birthDate" />}
            value={expert.birthdate}
            onChange={(event) => handleChange({ [PropertyNames.birthdate]: event === null ? undefined : event })}
            required={true}
            error={requiredFields.isBirthDateEmpty}
          />
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <AhvNoTextField
            error={requiredFields.isAhvNoEmpty}
            required={true}
            label={<TranslatedMessage id="person.field.ahvNumber" />}
            onChangeIsValid={props.handleAhvIsValid}
            onChange={(event) => handleChange({ [PropertyNames.ahvNumber]: event.target.value })}
            value={expert.ahvNumber || ""}
          />
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <CiceroNoTextField
            required={false}
            label={<TranslatedMessage id="person.field.ciceroNumber" />}
            onChangeIsValid={props.handleCiceroIsValid}
            onChange={(event) => handleChange({ [PropertyNames.ciceroNumber]: event.target.value })}
            value={expert.ciceroNumber || ""}
          />
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <EmailTextField
            error={requiredFields.isEmailEmpty}
            required
            label={<TranslatedMessage id="person.field.email" />}
            onChangeIsValid={props.handleEmailIsValid}
            onChange={(event) => handleChange({ [PropertyNames.emailAddress]: event.target.value })}
            value={expert.emailAddress || ""}
          />
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <PhoneNoTextField
            label={GetTranslation(intl, "person.field.phoneNumber")}
            onChangeIsValid={props.handlePhoneIsValid}
            onChange={(event) => handleChange({ [PropertyNames.phoneNumber]: event.target.value })}
            value={expert.phoneNumber || ""}
          />
        </Grid>
        <Grid item={true} xs={12} sm={6} md={4}>
          <PhoneNoTextField
            error={requiredFields.isMobileNoEmpty}
            required
            label={GetTranslation(intl, "person.field.mobileNumber")}
            onChangeIsValid={props.handleMobileIsValid}
            onChange={(event) => handleChange({ [PropertyNames.mobileNumber]: event.target.value })}
            value={expert.mobileNumber || ""}
          />
        </Grid>
        <GridTextField
          error={requiredFields.isBankAccountEmpty}
          required
          label={GetTranslation(intl, "person.field.bankAccount")}
          xs={12}
          sm={6}
          md={4}
          onChange={(event) => handleChange({ [PropertyNames.bankAccount]: event.target.value })}
          value={expert.bankAccount || ""}
        />
        <CompanySelect
          xs={12}
          sm={6}
          md={4}
          label={GetTranslation(intl, "person.field.employer")}
          value={expert.employerInformation}
          onChange={handleCompanyChanged}
        />
      </Grid>
    </>
  );
};

export default injectIntl(ExpertPersonalFields);
